/**
 * ProfileCompletionBanner — polls `/api/user/profile-completion` and
 * nudges members to finish the demographic + machine survey while
 * their onboarding intent is fresh. Backing the Analytics dashboard
 * needs this data — a low completion rate wrecks every downstream
 * insight. Dismissible for the current session so it doesn't harass.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { ClipboardList, X, ArrowRight } from 'lucide-react';

export default function ProfileCompletionBanner() {
  const { API } = useContext(AuthContext);
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(() =>
    (typeof window !== 'undefined' && sessionStorage.getItem('fdacademy_profile_banner_dismissed') === '1'),
  );

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/user/profile-completion`).then(({ data }) => {
      if (!cancel) setState(data);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [API]);

  if (dismissed || !state) return null;
  if (state.is_complete) return null;

  const pct = Math.max(0, Math.min(100, Math.round(state.completion_percentage || 0)));
  const missing = state.missing_fields || [];

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('fdacademy_profile_banner_dismissed', '1'); } catch (_) {}
  };

  return (
    <section className="container mx-auto px-6 pt-6" data-testid="profile-completion-banner">
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 sm:p-5 flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-xl bg-amber-500 text-navy flex items-center justify-center flex-shrink-0">
          <ClipboardList className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 dark:text-amber-300">
            Finish your profile — {pct}% complete
          </div>
          <h2 className="font-heading text-lg sm:text-xl font-extrabold text-foreground mt-1">
            {missing.length > 0
              ? `${missing.length} field${missing.length === 1 ? '' : 's'} left before your dashboard is fully personalized`
              : 'Just a few more fields left'}
          </h2>
          <p className="mt-1 text-xs text-foreground/70">
            Your machine + demographic data powers cohort insights, better AI recommendations, and event invites near you.
          </p>
          <div className="mt-3 h-1.5 rounded-full bg-amber-500/20 overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all"
              style={{ width: `${pct}%` }}
              data-testid="profile-completion-bar"
            />
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => { window.location.href = '/portal?tab=profile'; }}
              className="rounded-full font-bold bg-amber-500 text-navy hover:bg-amber-500/90"
              data-testid="profile-completion-cta"
            >
              Finish now <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            {missing.length > 0 && (
              <span className="text-[10px] text-muted-foreground truncate">
                Still missing: {missing.slice(0, 3).join(', ')}{missing.length > 3 ? ` +${missing.length - 3} more` : ''}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="h-7 w-7 rounded-full hover:bg-amber-500/20 flex items-center justify-center"
          aria-label="Dismiss for this session"
          data-testid="profile-completion-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
